import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TypeAnimation } from 'react-type-animation';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import './Home.css';

// ── Stats ──────────────────────────────────────────────────────────────────
const stats = [
  { value: 150, suffix: '+', label: 'Projects Delivered' },
  { value: 80, suffix: '+', label: 'Happy Clients' },
  { value: 12, suffix: '+', label: 'Years Experience' },
  { value: 99, suffix: '%', label: 'Client Satisfaction' },
];

// ── Services ───────────────────────────────────────────────────────────────
const services = [
  {
    icon: '🤖',
    title: 'AI Agents',
    desc: 'Autonomous AI agents that automate complex workflows, decision-making, and multi-step business processes.',
    tags: ['Gen AI', 'LLM', 'Automation'],
  },
  {
    icon: '💬',
    title: 'Chatbot Development',
    desc: 'Intelligent conversational bots trained on your data — support, sales, onboarding, and beyond.',
    tags: ['NLP', 'OpenAI', 'RAG'],
  },
  {
    icon: '🌐',
    title: 'Web Applications',
    desc: 'Scalable, performant MERN-stack web apps with modern UX and cloud-native architecture.',
    tags: ['React', 'Node.js', 'MongoDB'],
  },
  {
    icon: '📱',
    title: 'Mobile Apps',
    desc: 'Cross-platform iOS & Android apps built with React Native and native performance.',
    tags: ['React Native', 'iOS', 'Android'],
  },
  {
    icon: '🏥',
    title: 'Healthcare Apps',
    desc: 'HIPAA-compliant digital health platforms: patient portals, telemedicine, and EHR integrations.',
    tags: ['HIPAA', 'HL7', 'FHIR'],
  },
  {
    icon: '🛡️',
    title: 'Insurance Solutions',
    desc: 'End-to-end insurance platforms for quoting, policy management, claims, and compliance.',
    tags: ['InsurTech', 'Compliance', 'APIs'],
  },
];

// ── Tech stack ─────────────────────────────────────────────────────────────
const techStack = [
  { name: 'AWS', icon: '☁️' }, { name: 'Azure', icon: '🔷' }, { name: 'GCP', icon: '🌩️' },
  { name: 'React', icon: '⚛️' }, { name: 'Node.js', icon: '🟢' }, { name: 'MongoDB', icon: '🍃' },
  { name: 'Docker', icon: '🐳' }, { name: 'Kubernetes', icon: '☸️' }, { name: 'Jenkins', icon: '🔧' },
  { name: 'Python', icon: '🐍' }, { name: 'PostgreSQL', icon: '🐘' }, { name: 'Lambda', icon: 'λ' },
  { name: 'Gen AI', icon: '✨' }, { name: 'PHP', icon: '🐘' }, { name: 'JavaScript', icon: '🟡' },
];

// ── Why us ─────────────────────────────────────────────────────────────────
const whyUs = [
  { icon: '🚀', title: 'Fast Delivery', desc: 'Agile sprints with CI/CD pipelines ensure rapid, reliable delivery.' },
  { icon: '🔒', title: 'Security First', desc: 'Enterprise-grade security, compliance, and penetration-tested codebases.' },
  { icon: '📈', title: 'Scalable by Design', desc: 'Cloud-native, microservices architecture that grows with your business.' },
  { icon: '🤝', title: 'Dedicated Teams', desc: 'Embedded engineers who work as an extension of your own team.' },
];

// ── StatCounter Component ──────────────────────────────────────────────────
function StatCounter({ value, suffix, label }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.5 });
  return (
    <div className="stat-item" ref={ref}>
      <div className="stat-number">
        {inView ? <CountUp end={value} duration={2.5} /> : '0'}
        <span className="stat-suffix">{suffix}</span>
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

// ── Home Page ──────────────────────────────────────────────────────────────
const Home = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    // Re-trigger reveal on home mount
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('active'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="home">
      {/* ── HERO ──────────────────────────────────────────── */}
      <section className="hero" ref={heroRef}>
        <div className="hero-bg-mesh"></div>
        <div className="hero-content section-wrapper">
          <div className="hero-badge reveal">
            <span className="badge-dot"></span>
            Expand · Innovate · Transform
          </div>
          <h1 className="hero-title reveal delay-100">
            Building the Future with{' '}
            <span className="hero-typed">
              <TypeAnimation
                sequence={[
                  'AI Agents', 2000,
                  'Smart Chatbots', 2000,
                  'Cloud Solutions', 2000,
                  'Web & Mobile Apps', 2000,
                  'Gen AI Capabilities', 2000,
                ]}
                wrapper="span"
                speed={50}
                repeat={Infinity}
                className="typed-text"
              />
            </span>
          </h1>
          <p className="hero-desc reveal delay-200">
            VistaraKlyQs Tech LLP delivers cutting-edge software solutions — from autonomous AI agents to
            full-stack web & mobile apps — powered by the latest cloud and GenAI technologies.
          </p>
          <div className="hero-actions reveal delay-300">
            <Link to="/services" className="btn-primary">Explore Services</Link>
            <Link to="/contact" className="btn-outline">Talk to Us</Link>
          </div>
          <div className="hero-tech-scroll reveal delay-400">
            <span className="tech-scroll-label">Tech we master →</span>
            <div className="tech-chips">
              {['AWS', 'React', 'Node.js', 'Docker', 'Gen AI', 'Python', 'Kubernetes', 'MongoDB'].map((t) => (
                <span key={t} className="tech-chip">{t}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="hero-visual reveal-right delay-200">
          <div className="circuit-tree">
            <svg viewBox="0 0 300 350" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <radialGradient id="glowGrad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#00d4d4" stopOpacity="0.3"/>
                  <stop offset="100%" stopColor="#00d4d4" stopOpacity="0"/>
                </radialGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
                </filter>
              </defs>
              {/* Glow bg */}
              <ellipse cx="150" cy="175" rx="130" ry="150" fill="url(#glowGrad)"/>
              {/* Ground arc */}
              <path d="M80 280 Q150 265 220 280" stroke="#00d4d4" strokeWidth="2" fill="none" filter="url(#glow)"/>
              {/* Trunk */}
              <line x1="150" y1="280" x2="150" y2="120" stroke="#00d4d4" strokeWidth="3" filter="url(#glow)"/>
              {/* Main branches */}
              <line x1="150" y1="200" x2="80" y2="160" stroke="#00d4d4" strokeWidth="2" filter="url(#glow)"/>
              <line x1="150" y1="200" x2="220" y2="160" stroke="#00d4d4" strokeWidth="2" filter="url(#glow)"/>
              <line x1="150" y1="170" x2="60" y2="140" stroke="#00d4d4" strokeWidth="1.5" filter="url(#glow)"/>
              <line x1="150" y1="170" x2="240" y2="140" stroke="#00d4d4" strokeWidth="1.5" filter="url(#glow)"/>
              <line x1="150" y1="150" x2="100" y2="110" stroke="#00d4d4" strokeWidth="1.5" filter="url(#glow)"/>
              <line x1="150" y1="150" x2="200" y2="110" stroke="#00d4d4" strokeWidth="1.5" filter="url(#glow)"/>
              <line x1="150" y1="135" x2="120" y2="90" stroke="#00d4d4" strokeWidth="1" filter="url(#glow)"/>
              <line x1="150" y1="135" x2="180" y2="90" stroke="#00d4d4" strokeWidth="1" filter="url(#glow)"/>
              <line x1="150" y1="125" x2="150" y2="80" stroke="#00d4d4" strokeWidth="1.5" filter="url(#glow)"/>
              {/* Horizontal connector lines (circuit feel) */}
              <line x1="80" y1="160" x2="60" y2="160" stroke="#00d4d4" strokeWidth="1" filter="url(#glow)"/>
              <line x1="220" y1="160" x2="240" y2="160" stroke="#00d4d4" strokeWidth="1" filter="url(#glow)"/>
              <line x1="60" y1="140" x2="40" y2="140" stroke="#00d4d4" strokeWidth="1" filter="url(#glow)"/>
              <line x1="240" y1="140" x2="260" y2="140" stroke="#00d4d4" strokeWidth="1" filter="url(#glow)"/>
              {/* End nodes */}
              {[
                [150, 75], [120, 85], [180, 85], [100, 105], [200, 105],
                [60, 135], [240, 135], [78, 155], [222, 155],
                [40, 140], [260, 140],
              ].map(([cx, cy], i) => (
                <g key={i}>
                  <circle cx={cx} cy={cy} r="5" fill="none" stroke="#00d4d4" strokeWidth="1.5" filter="url(#glow)"/>
                  <circle cx={cx} cy={cy} r="2.5" fill="#00d4d4" filter="url(#glow)"/>
                </g>
              ))}
            </svg>
          </div>
        </div>
      </section>

      {/* ── STATS ─────────────────────────────────────────── */}
      <section className="stats-section">
        <div className="section-wrapper stats-grid">
          {stats.map((s, i) => (
            <StatCounter key={i} {...s} />
          ))}
        </div>
      </section>

      {/* ── SERVICES PREVIEW ──────────────────────────────── */}
      <section className="services-section">
        <div className="section-wrapper">
          <div className="section-header reveal">
            <span className="section-tag">What We Build</span>
            <h2 className="section-title">Services that <span>Drive Growth</span></h2>
            <p className="section-subtitle">
              From AI-powered products to enterprise cloud platforms, we deliver end-to-end solutions tailored to your industry.
            </p>
          </div>
          <div className="services-grid">
            {services.map((s, i) => (
              <div key={i} className={`service-card glass-card reveal delay-${(i % 3) * 100 + 100}`}>
                <div className="service-icon">{s.icon}</div>
                <h3 className="service-title">{s.title}</h3>
                <p className="service-desc">{s.desc}</p>
                <div className="service-tags">
                  {s.tags.map((t) => <span key={t} className="service-tag">{t}</span>)}
                </div>
              </div>
            ))}
          </div>
          <div className="reveal" style={{ textAlign: 'center', marginTop: '3rem' }}>
            <Link to="/services" className="btn-outline">View All Services</Link>
          </div>
        </div>
      </section>

      {/* ── TECH STACK ────────────────────────────────────── */}
      <section className="tech-section">
        <div className="section-wrapper">
          <div className="section-header reveal">
            <span className="section-tag">Technology Stack</span>
            <h2 className="section-title">Powered by <span>Best-in-Class</span> Tools</h2>
          </div>
          <div className="tech-grid">
            {techStack.map((t, i) => (
              <div key={i} className={`tech-pill reveal delay-${(i % 5) * 100}`}>
                <span className="tech-pill-icon">{t.icon}</span>
                <span>{t.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY US ────────────────────────────────────────── */}
      <section className="why-section">
        <div className="section-wrapper why-grid">
          <div className="why-text reveal-left">
            <span className="section-tag">Why VistaraKlyQs</span>
            <h2 className="section-title">Your trusted tech <span>transformation</span> partner</h2>
            <p className="section-subtitle">
              We combine deep technical expertise with industry knowledge to deliver
              solutions that move the needle — on time, within budget, and built to last.
            </p>
            <Link to="/about" className="btn-primary" style={{ marginTop: '2rem', display: 'inline-flex' }}>
              Learn More About Us
            </Link>
          </div>
          <div className="why-cards">
            {whyUs.map((w, i) => (
              <div key={i} className={`why-card glass-card reveal-right delay-${i * 100 + 100}`}>
                <span className="why-icon">{w.icon}</span>
                <div>
                  <h4 className="why-title">{w.title}</h4>
                  <p className="why-desc">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────── */}
      <section className="cta-section">
        <div className="cta-glow"></div>
        <div className="section-wrapper cta-inner reveal">
          <h2 className="cta-title">Ready to Build Something <span>Amazing?</span></h2>
          <p className="cta-sub">Tell us your vision — we'll bring it to life with cutting-edge tech and expert execution.</p>
          <div className="cta-actions">
            <Link to="/contact" className="btn-primary">Start a Project</Link>
            <Link to="/services" className="btn-outline">Browse Services</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
